import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Kanban, List, PackageX } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { PageHeader, StatusBadge, Tone } from "@/components/shared";
import { fdate, inr, useApi } from "@/lib/api";

function Card({ p }) {
  return (
    <Link to={`/projects/${p.id}`} className="block rounded-md border bg-card p-3 hover:border-foreground/25 transition-colors" data-testid={`project-card-${p.projectCode}`}>
      <div className="flex justify-between items-center"><span className="font-mono text-xs font-semibold">{p.projectCode}</span><StatusBadge status={p.status} /></div>
      <div className="text-sm font-medium mt-2">{p.customerName}</div>
      <div className="text-xs text-muted-foreground">{p.workflowName}</div>
      <Progress value={p.progressPct} className="h-1 mt-3" />
      <div className="flex justify-between mt-2 text-[11px] text-muted-foreground"><span className="num">{inr(p.grandTotal)}</span>
        {p.toProcureCount > 0 && <span className="flex items-center gap-1 text-amber-600"><PackageX className="h-3 w-3" />{p.toProcureCount} to procure</span>}</div>
    </Link>
  );
}

export default function Projects() {
  const { data } = useApi("/projects");
  const { data: wfs } = useApi("/workflows");
  const [view, setView] = useState("list");
  const nav = useNavigate();
  const stageNames = [...new Set([...(wfs || []).flatMap((w) => w.stages.map((s) => s.stageName)), ...(data || []).map((p) => p.currentStageName)])];
  const cols = [...stageNames.filter((s) => s !== "Completed" && s !== "-"), "Completed"];
  return (
    <div>
      <PageHeader title="Projects" subtitle="Builds grouped by their current workflow stage.">
        <Tabs value={view} onValueChange={setView}><TabsList>
          <TabsTrigger value="kanban" data-testid="projects-view-kanban"><Kanban className="h-4 w-4 mr-1.5" />Board</TabsTrigger>
          <TabsTrigger value="list" data-testid="projects-view-list"><List className="h-4 w-4 mr-1.5" />List</TabsTrigger>
        </TabsList></Tabs>
      </PageHeader>
      {view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-4 snap-x" data-testid="projects-kanban">
          {cols.map((c) => {
            const items = (data || []).filter((p) => p.currentStageName === c);
            return (
              <div key={c} className="w-[78vw] sm:w-64 shrink-0 snap-start rounded-lg bg-muted/40 border p-2.5" data-testid={`kanban-col-${c.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="flex justify-between px-1 pb-2.5"><span className="text-xs font-semibold">{c}</span><span className="text-xs text-muted-foreground num">{items.length}</span></div>
                <div className="space-y-2 min-h-[80px]">{items.map((p) => <Card key={p.id} p={p} />)}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="projects-table">
            <thead><tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              {["Project", "Customer", "Workflow", "Current stage", "Progress", "Parts", "Balance due", "Status", "Created"].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody>{data && !data.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="projects-empty">No projects yet. Convert an approved quotation to start a build.</td></tr>}
              {(data || []).map((p) => (
              <tr key={p.id} onClick={() => nav(`/projects/${p.id}`)} className="border-b last:border-0 cursor-pointer hover:bg-secondary/30" data-testid={`project-row-${p.projectCode}`}>
                <td className="px-4 py-3 font-mono text-xs font-semibold">{p.projectCode}</td><td className="px-4 py-3">{p.customerName}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.workflowName}</td><td className="px-4 py-3">{p.currentStageName}</td>
                <td className="px-4 py-3 w-32"><Progress value={p.progressPct} className="h-1.5" /></td>
                <td className="px-4 py-3">{p.toProcureCount ? <Tone tone="amber">{p.toProcureCount} to procure</Tone> : <Tone tone="green">All in stock</Tone>}</td>
                <td className="px-4 py-3 num">{inr(p.balanceDueAmount)}</td><td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{fdate(p.createdAt)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
